import { useTdsCommon } from '../../hooks/useTdsCommon'
import { getStateCode, formatDate, formatDecimalAmount, padString, padNumber, getTdsFormRoute } from '../../utils/tdsCommon'
import { extractProfileDataApi } from '../../utils/tdsFileHandlers'
import React, { useState, useEffect, useCallback, useRef } from 'react'

import { motion } from 'framer-motion'
import { FileText, User, Upload, ArrowLeft, Building2, X, Trash2, Eye, EyeOff } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import axios from 'axios'
import TdsDetailsTable from '../../components/dashboard/TdsDetailsTable'
import ChallanDetailsTable from '../../components/dashboard/ChallanDetailsTable'
import TdsSidebar from '../../components/dashboard/TdsSidebar'
import { useError } from '../../contexts/ErrorContext'
import Tds24Q4Form from './Tds24Q4Form'
import Tds24QCorrectionForm from './Tds24QCorrectionForm'
import TdsProfileSection from '../../components/dashboard/TdsProfileSection';



const stateOptions = [
  'ANDAMAN AND NICOBAR ISLANDS',
  'ANDHRA PRADESH',
  'ARUNACHAL PRADESH',
  'ASSAM',
  'BIHAR',
  'CHANDIGARH',
  'CHHATTISGARH',
  'DADRA & NAGAR HAVELI AND DAMAN & DIU',
  'DELHI',
  'GOA',
  'GUJARAT',
  'HARYANA',
  'HIMACHAL PRADESH',
  'JAMMU & KASHMIR',
  'JHARKHAND',
  'KARNATAKA',
  'KERALA',
  'LADAKH',
  'LAKSHWADEEP',
  'MADHYA PRADESH',
  'MAHARASHTRA',
  'MANIPUR',
  'MEGHALAYA',
  'MIZORAM',
  'NAGALAND',
  'ODISHA',
  'PONDICHERRY',
  'PUNJAB',
  'RAJASTHAN',
  'SIKKIM',
  'TAMIL NADU',
  'TELANGANA',
  'TRIPURA',
  'UTTAR PRADESH',
  'UTTARAKHAND',
  'WEST BENGAL'
];

const sectionOptions24Q_old = [
  { value: '92A', label: '92A - 192 (Payment to Govt. Employees other than Union Govt.)' },
  { value: '92B', label: '92B - 192 (Payment to Employees other than Govt. Employees)' },
  { value: '92C', label: '92C - 192 (Payment to Union Govt. Employees)' },
  { value: '94P', label: '94P - 194P (Specified Senior Citizens)' }
];

const sectionOptions24Q_new = [
  { value: '1001', label: '1001 - Salary – State Government Employees' },
  { value: '1002', label: '1002 - Salary – Non-Government/Private Sector Employees' },
  { value: '1003', label: '1003 - Salary – Union/Central Government Employees' },
  { value: '1032', label: '1032 - Specified Senior Citizen Pension & Interest' }
];

const Tds24QForm = ({ isMobile, onSidebarStateChange  }) => {
    const csiFileInputRef = useRef(null);
    console.log('Rendering Tds24QForm component');
    const navigate = useNavigate()
    const { addError } = useError()
    const [activeSection, setActiveSection] = useState('profile')
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [sidebarState, setSidebarState] = useState({
        isOpen: false,
        isExpanded: false,
        keepOpen: false,
        hovered: false
    })

    const {
        profileData,
        challanData,
        tdsData,
        originalTdsData,
        csiFile,
        consoFileContent,
        isValidating,
        showCsiUploadPopup,
        showDownloadPrompt,
        selectedFinancialYear,
        selectedQuarter,
        selectedForm,
        selectedReturnType,
        challanDateRange,
        setProfileData,
        setChallanData,
        setTdsData,
        setOriginalTdsData,
        setCsiFile,
        setConsoFileContent,
        setIsValidating,
        setShowCsiUploadPopup,
        setShowDownloadPrompt,
        setSelectedFinancialYear,
        setSelectedQuarter,
        setSelectedForm,
        setSelectedReturnType,
        handlePrepareReturnFile,
        handleValidateReturn,
        handleSkipDownload,
        handleCancelValidation,
        executeValidation
    } = useTdsCommon({
        formName: 'tds24Q',
        defaultFormType: '24Q',
        isCorrection: false,
        hasQ4Child: true,
        generateTdsStatementFile,
        validateDataForReturn,
        onValidationError: (errors) => {
            addError('Please fix the following errors before preparing return:')
            errors.forEach(err => addError(`• ${err}`))
            setSidebarOpen(true)
            setActiveSection('errors')
        }
    });

    const [isProfileEditing, setIsProfileEditing] = useState(true)
    const [downloadPassword, setDownloadPassword] = useState('')
    const [showDownloadPassword, setShowDownloadPassword] = useState(false)
    const [isDownloadingCsi, setIsDownloadingCsi] = useState(false)
    const handleCsiDownload = async () => {
        if (!downloadPassword) {
            addError('Password is required');
            return;
        }

        setIsDownloadingCsi(true);
        try {
            const response = await axios.post('/api/login-income-tax', {
                credentials: {
                    tan: profileData?.section1?.taxDeduction,
                    password: downloadPassword
                },
                dateRange: challanDateRange
            });

            if (response.data.success && response.data.csiContent) {
                toast.success('CSI File Downloaded Successfully!');
                setShowDownloadPrompt(false);
                setDownloadPassword('');
                
                const binaryString = window.atob(response.data.csiContent.replace(/\s/g, ''));
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const file = new File([bytes], response.data.fileName || 'downloaded.csi', { type: 'text/plain' });
                if (typeof setCsiFile === 'function') setCsiFile(file);
                if (typeof executeValidation === 'function') executeValidation(file);
            } else {
                const errMsg = response.data.error || 'Failed to download CSI file. Please check password.';
                toast.error(errMsg);
                if (typeof addError === 'function') addError(errMsg);
            }
        } catch (err) {
            console.error(err);
            const serverErr = err.response?.data?.error || 'Failed to initiate download';
            toast.error(serverErr);
            if (typeof addError === 'function') addError(serverErr);
        } finally {
            setIsDownloadingCsi(false);
        }
    };


    // Profile Pre-fill Automation State
    const [showPrefillPrompt, setShowPrefillPrompt] = useState(false);
    const [prefillPassword, setPrefillPassword] = useState('');
    const [showPrefillPassword, setShowPrefillPassword] = useState(false);
    const [isExtractingProfile, setIsExtractingProfile] = useState(false)

    const handleProfilePrefill = async () => {
        if (!prefillPassword) {
            toast.error('Portal password is required');
            return;
        }

        setIsExtractingProfile(true);
        try {
            const { success, data, error } = await extractProfileDataApi({
                tan: profileData?.section1?.taxDeduction,
                password: prefillPassword
            });

            if (success && data) {
                const newData = { ...profileData };
                if (data.pan) {
                    if (newData.section1) newData.section1.permanentAccountNumber = data.pan;
                    if (newData.section3) newData.section3.permanentAccountNumber = data.pan;
                }
                if (data.name) {
                    if (newData.section2) newData.section2.name = data.name;
                }
                if (data.keyPerson) {
                    if (data.keyPerson.name && newData.section3) newData.section3.name = data.keyPerson.name;
                    if (data.keyPerson.pan && newData.section3) newData.section3.permanentAccountNumber = data.keyPerson.pan;
                }
                if (data.email) {
                    if (newData.section2) newData.section2.email = data.email;
                    if (newData.section3) newData.section3.email = data.email;
                }
                if (data.mobile) {
                    if (newData.section2) newData.section2.mobileNo = data.mobile;
                    if (newData.section3) newData.section3.mobileNo = data.mobile;
                }
                if (data.address) {
                    if (newData.section2) {
            newData.section2.flatNo = data.address.flatNo || '';
            newData.section2.nameOfPremises = data.address.premisesName || '';
            newData.section2.roadStreet = data.address.roadStreet || '';
            newData.section2.areaLocality = data.address.areaLocality || '';
            newData.section2.townCity = data.address.city || '';
            newData.section2.pinCode = data.address.pinCode || '';
            if (data.address.state) {
                newData.section2.state = data.address.state.toUpperCase().trim();
            }
          }
                }
                
                if (typeof setProfileData === 'function') {
                    setProfileData(newData);
                }
                toast.success('Profile Data Auto-Filled Successfully!');
                setShowPrefillPrompt(false);
                setPrefillPassword('');
            } else {
                toast.error(error || 'Failed to extract profile data');
            }
        } catch (err) {
            console.error(err);
            toast.error('An error occurred during extraction');
        } finally {
            setIsExtractingProfile(false);
        }
    };
;

    const handleChallanDataChange = setChallanData;
    const handleTdsDataChange = setTdsData;



  const handleSectionChange = (section) => {
    setActiveSection(section)
  }

  const handleSidebarClose = () => {
    setSidebarOpen(false)
  }

  const handleSidebarStateChange = useCallback((state) => {
    setSidebarState(state)
  }, [])



  // Sync Section 3 address fields with Section 2 when "Same as above" is checked
  useEffect(() => {
    // Add safety checks to ensure section2 and section3 exist
    if (
      profileData?.section3?.sameAsAbove &&
      isProfileEditing &&
      profileData?.section2 &&
      profileData?.section3
    ) {
      const newData = { ...profileData }
      // Ensure section2 and section3 exist in newData
      if (!newData.section2) newData.section2 = {}
      if (!newData.section3) newData.section3 = {}

      newData.section3.nameOfPremises = newData.section2.nameOfPremises || ''
      newData.section3.flatNo = newData.section2.flatNo || ''
      newData.section3.areaLocality = newData.section2.areaLocality || ''
      newData.section3.roadStreet = newData.section2.roadStreet || ''
      newData.section3.townCity = newData.section2.townCity || ''
      newData.section3.pinCode = newData.section2.pinCode || ''
      newData.section3.state = newData.section2.state || ''
      newData.section3.email = newData.section2.email || ''
      setProfileData(newData)
      // Cloud sync via updateProfileData)
    }
  }, [
    profileData?.section2?.nameOfPremises,
    profileData?.section2?.flatNo,
    profileData?.section2?.areaLocality,
    profileData?.section2?.roadStreet,
    profileData?.section2?.townCity,
    profileData?.section2?.pinCode,
    profileData?.section2?.state,
    profileData?.section2?.email,
    profileData?.section3?.sameAsAbove,
    isProfileEditing
  ])

  // Profile form handlers
  const handleProfileInputChange = (field, value) => {
    const keys = field.split('.')
    const newData = { ...profileData }

    // Ensure section1, section2, section3 exist
    if (!newData.section1) newData.section1 = {}
    if (!newData.section2) newData.section2 = {}
    if (!newData.section3) newData.section3 = {}

    let current = newData
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {}
      }
      current = current[keys[i]]
    }
    current[keys[keys.length - 1]] = value

    // If "Same as above" is checked, copy address fields from section2 to section3
    if (field === 'section3.sameAsAbove' && value === true && newData.section2 && newData.section3) {
      newData.section3.nameOfPremises = newData.section2.nameOfPremises || ''
      newData.section3.flatNo = newData.section2.flatNo || ''
      newData.section3.areaLocality = newData.section2.areaLocality || ''
      newData.section3.roadStreet = newData.section2.roadStreet || ''
      newData.section3.townCity = newData.section2.townCity || ''
      newData.section3.pinCode = newData.section2.pinCode || ''
      newData.section3.state = newData.section2.state || ''
      newData.section3.email = newData.section2.email || ''
      // Note: mobileNo is not in section2, so we don't copy it
    }
    setProfileData(newData)
    // Cloud sync via updateProfileData)
  }



  // Validation function for Prepare Return
  function validateDataForReturn() {
    const errors = []

    // Check if at least one TDS entry exists
    if (!tdsData || tdsData.length === 0) {
      errors.push('At least one TDS entry is required')
    }

    // Check if at least one Challan entry exists
    if (!challanData || challanData.length === 0) {
      errors.push('At least one Challan entry is required')
    }

    // Receipt Details Validation
    if (selectedReturnType === 'Correction') {
      if (!profileData?.section1?.receiptNoOriginal) {
        errors.push('Receipt No. of Original Return is missing (Upload valid Conso File)')
      }
      if (!profileData?.section1?.receiptNoEarlier) {
        errors.push('Receipt No. of Earlier Statement is missing (Upload valid Conso File)')
      }
    } else if (selectedReturnType === 'Regular') {
      if (profileData?.section1?.isEarlierReturnFiled === 'Yes' && !profileData?.section1?.receiptNoOriginal) {
        errors.push('Receipt No. of Original Return is required')
      }
    }

    // Validate profile data - check compulsory fields with safety checks
    if (!profileData?.section2?.name) {
      errors.push('Name is required in Profile Section 2')
    }
    if (!profileData?.section2?.flatNo) {
      errors.push('Flat No. is required in Profile Section 2')
    }
    if (!profileData?.section2?.state) {
      errors.push('State is required in Profile Section 2')
    }
    if (!profileData?.section2?.pinCode) {
      errors.push('PIN Code is required in Profile Section 2')
    }
    if (!profileData?.section3?.name) {
      errors.push('Responsible Person Name is required in Profile')
    }
    if (!profileData?.section3?.designation) {
      errors.push('Responsible Person Designation is required in Profile')
    }
    if (!profileData?.section3?.flatNo && !profileData?.section3?.nameOfPremises && !profileData?.section3?.roadStreet) {
      errors.push('Responsible Person Address is required in Profile')
    }
    if (!profileData?.section3?.state) {
      errors.push('Responsible Person State is required in Profile')
    }
    if (!profileData?.section3?.pinCode) {
      errors.push('Responsible Person Pincode is required in Profile')
    }
    if (!profileData?.section3?.email) {
      errors.push('Responsible Person Email is required in Profile')
    }
    if (!profileData?.section3?.mobileNo) {
      errors.push('Responsible Person Mobile is required in Profile')
    }
    if (!profileData?.section3?.permanentAccountNumber) {
      errors.push('Responsible Person PAN is required in Profile')
    }

    // Validate TDS data - check compulsory fields
    if (tdsData && tdsData.length > 0) {
      tdsData.forEach((row, index) => {
        if (!row.panOfDeductee) {
          errors.push(`TDS Entry ${index + 1}: PAN of Deductee is required`)
        }
        if (!row.sectionCode) {
          errors.push(`TDS Entry ${index + 1}: Section Code is required`)
        }
        if (row.tdsAmount === '' || row.tdsAmount === null || row.tdsAmount === undefined) {
          row.tdsAmount = 0;
        }
        if (!row.dateOfPayment) {
          errors.push(`TDS Entry ${index + 1}: Date of Payment is required`)
        }
        if (!row.dateOfDeduction && (parseFloat(row.tdsAmount || 0) + parseFloat(row.surcharge || 0) + parseFloat(row.cess || 0)) > 0) {
          errors.push(`TDS Entry ${index + 1}: Date of Deduction is required`)
        }
        if (parseFloat(row.tdsAmount) > 0 && !row.challanId) {
          errors.push(`TDS Entry ${index + 1}: Challan Selection is required`)
        }
      })
    }

    // Validate Challan data - check compulsory fields
    if (challanData && challanData.length > 0) {
      challanData.forEach((row, index) => {
        if (!row.tdsAmount || parseFloat(row.tdsAmount) <= 0) {
          errors.push(`Challan Entry ${index + 1}: TDS Amount is required`)
        }
        if (!row.dateOfDeposit) {
          errors.push(`Challan Entry ${index + 1}: Date of Deposit is required`)
        }
        if (!row.bsrCode) {
          errors.push(`Challan Entry ${index + 1}: BSR Code is required`)
        } else if (row.bsrCode.length !== 7) {
          errors.push(`Challan Entry ${index + 1}: BSR Code must be 7 digits`)
        }
        if (!row.challanSerialNo) {
          errors.push(`Challan Entry ${index + 1}: Challan Serial No. is required`)
        } else if (row.challanSerialNo.length !== 5) {
          errors.push(`Challan Entry ${index + 1}: Challan Serial No. must be 5 digits`)
        }
      })
    }

    return errors
  }

  // File generation function
  function generateTdsStatementFile() {
    const currentDate = new Date()
    const fileCreationDate = currentDate.toLocaleDateString('en-GB').replace(/\//g, '')

    // Get financial year for assessment year calculation
    let fyStartYear = 0
    let financialYear = ''

    if (selectedFinancialYear) {
      const parts = selectedFinancialYear.split('-')
      if (parts.length >= 2) {
        fyStartYear = parseInt(parts[0])
        // Handle both 2025-26 and 2025-2026 formats to produce 202526
        const yearPart2 = parts[1].length === 4 ? parts[1].slice(-2) : parts[1]
        financialYear = `${parts[0]}${yearPart2}`
      }
    }

    // Fallback if invalid
    if (!fyStartYear || isNaN(fyStartYear)) {
      const today = new Date()
      fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1
      financialYear = `${fyStartYear}${String(fyStartYear + 1).slice(-2)}`
    }

    const ayStart = fyStartYear + 1
    const assessmentYear = `${ayStart}${String(ayStart + 1).slice(-2)}`

    console.log('Generating File with:', { selectedFinancialYear, financialYear, assessmentYear })

    // Format dates to ddmmyyyy
    

    // Format amounts to remove decimals and pad with zeros
    const formatAmount = (amount) => {
      const num = parseFloat(amount || 0)
      return Math.round(num).toString().padStart(15, '0')
    }

    // Format decimal amounts with 2 decimal places
    

    // Truncate strings to max length (no padding needed for delimited file)
    

    const formatMinorHead = (val) => {
      if (!val) return ''
      const s = String(val).trim()
      // If it's already a code, return it
      if (s === '200' || s === '400') return s

      // Map common descriptions to codes
      const lower = s.toLowerCase()
      if (lower.includes('payable') || lower.includes('taxpayer')) return '200'
      if (lower.includes('regular') || lower.includes('assessment')) return '400'

      // Extract code if present in string (e.g. "TDS payable by taxpayer (200)")
      const match = s.match(/(200|400)/)
      if (match) return match[0]

      return s
    }

    // State Code Mapping
    

          

    // Pad numbers to required length (Truncate/Strip logic if needed? previous padNumber was padding with 0s)
    // For Batch Total we used formatDecimalAmount
    // For AY we used custom logic
    // For State Code we return string 09
    const padNumber = (num, length) => {
      // Keep existing padNumber logic for other fields if any
      return String(num || 0).padStart(length, '0')
    }

    let fileContent = ''
    let lineNumber = 1

    // Ensure profileData has required sections
    const section1 = profileData?.section1 || {}
    const section2 = profileData?.section2 || {}
    const section3 = profileData?.section3 || {}

    // 1. File Header Record (FH)
    const fh = []
    fh[1] = lineNumber // 1. Line Number
    fh[2] = 'FH' // 2. Record Type
    fh[3] = 'SL1' // 3. File Type
    fh[4] = 'R' // 4. Upload Type
    fh[5] = fileCreationDate // 5. File Creation Date
    fh[6] = '1' // 6. File Sequence No.
    fh[7] = 'D' // 7. Uploader Type
    fh[8] = section1.taxDeduction || '' // 8. TAN of Employer
    fh[9] = '1' // 9. Total No. of Batches
    fh[10] = 'Tax Eases Client' // 10. Name of Return Preparation Utility
    fh[11] = '' // 11. Record Hash (Not applicable)
    fh[12] = '' // 12. FVU Version (Not applicable)
    fh[13] = '' // 13. File Hash (Not applicable)
    fh[14] = '' // 14. Sam Version (Not applicable)
    fh[15] = '' // 15. SAM Hash (Not applicable)
    fh[16] = '' // 16. SCM Version (Not applicable)
    fh[17] = '' // 17. SCM Hash (Not applicable)
    fh[18] = '' // 18. Consolidated file hash

    fileContent += fh.slice(1).join('^') + '\n'
    lineNumber++

    // 2. Batch Header Record (BH)
    const bh = []
    const batchTotal = challanData.reduce((sum, challan) => sum + parseFloat(challan.totalAmount || 0), 0)
    const deductorAddress1 = `${section2.flatNo || ''} ${section2.nameOfPremises || ''} ${section2.roadStreet || ''}`.trim()
    const responsiblePersonAddress1 = `${section3.flatNo || ''} ${section3.nameOfPremises || ''} ${section3.roadStreet || ''}`.trim()

    const receiptNo7 = ''
    const receiptNo8 = section1.isEarlierReturnFiled === 'Yes' ? (section1.receiptNoOriginal || '') : ''

    bh[1] = lineNumber // 1. Line Number
    bh[2] = 'BH' // 2. Record Type
    bh[3] = '1' // 3. Batch Number
    bh[4] = challanData.length // 4. Count of Challan/transfer voucher Records
    const isNewFormat = fyStartYear >= 2026;
    bh[5] = isNewFormat ? '138' : '24Q' // 5. Form Number (Fixed from 26Q)
    bh[6] = '' // 6. Transaction Type
    bh[7] = '' // 7. Batch Updation Indicator
    bh[8] = receiptNo7 // 8. Original Token Number
    bh[9] = receiptNo8 // 9. Token no. of previous regular statement
    bh[10] = '' // 10. Token Number of the statement submitted
    bh[11] = '' // 11. Token Number date
    bh[12] = '' // 12. Last TAN of Deductor / Employer / Collector ( Used for Verification) (Not applicable)
    bh[13] = section1.taxDeduction || '' // 13. TAN of Deductor / Employer
    bh[14] = '' // 14. Receipt number (eight digit) provided by TIN
    bh[15] = section1.permanentAccountNumber || '' // 15. PAN of Deductor / Employer
    bh[16] = assessmentYear // 16. Assessment Yr
    bh[17] = financialYear // 17. Financial Yr
    bh[18] = selectedQuarter // 18. Period
    bh[19] = padString(section2.name || '', 75) // 19. Name of Employer / Deductor
    bh[20] = padString(section2.branchDivision || '', 75) // 20. Employer / Deductor Branch/ Division
    bh[21] = padString(deductorAddress1, 25) // 21. Employer / Deductor Address1
    bh[22] = padString(section2.areaLocality || '', 25) // 22. Employer / Deductor Address2
    bh[23] = padString(section2.townCity || '', 25) // 23. Employer / Deductor Address3
    bh[24] = '' // 24. Address4
    bh[25] = '' // 25. Address5
    bh[26] = getStateCode(section2.state) // 26. State
    bh[27] = section2.pinCode || '' // 27. PIN
    const deductorStdCode = section2.stdCode || '91'
    const respPersonStdCode = (section3.sameAsAbove ? section2.stdCode : section3.stdCode) || section2.stdCode || '91'
    const respPersonTelephone = (section3.sameAsAbove ? section2.telephoneNo : (section3.telephone || section3.telephoneNo)) || ''

    bh[28] = padString(section2.email || '', 75) // 28. Email
    bh[29] = deductorStdCode // 29. STD Code
    bh[30] = section2.telephoneNo || '' // 30. Telephone
    bh[31] = 'N' // 31. Change of Address (Deductor)
    bh[32] = section1.typeOfDeduction || '' // 32. Deductor Type
    bh[33] = padString(section3.name || '', 75) // 33. Responsible Person Name
    bh[34] = padString(section3.designation || '', 20) // 34. Designation
    bh[35] = padString(responsiblePersonAddress1, 25) // 35. Responsible Person Address1
    bh[36] = padString(section3.areaLocality || '', 25) // 36. Address2
    bh[37] = padString(section3.townCity || '', 25) // 37. Address3
    bh[38] = '' // 38. Address4
    bh[39] = '' // 39. Address5
    bh[40] = getStateCode(section3.state) // 40. Responsible Person's State
    bh[41] = section3.pinCode || '' // 41. Responsible Person's PIN
    bh[42] = padString(section3.email || '', 75) // 42. Responsible Person's Email ID
    bh[43] = section3.mobileNo || '' // 43. Mobile number
    bh[44] = respPersonStdCode // 44. Responsible Person's STD Code
    bh[45] = respPersonTelephone // 45. Responsible Person's Tel-Phone No.
    bh[46] = section3.hasAddressChanged === 'Yes' ? 'Y' : 'N' // 46. Change of Address of Responsible person since last Return
    bh[47] = formatDecimalAmount(batchTotal) // 47. Batch Total
    bh[48] = '' // 48. Unmatched challan count
    bh[49] = '0' // 49. Count Salary Records
    bh[50] = '' // 50. Total Salary Income
    bh[51] = 'N' // 51. AO Approval
    bh[52] = section1.isEarlierReturnFiled === 'Yes' ? 'Y' : 'N' // 52. Whether regular statement filed for earlier period
    bh[53] = '' // 53. Last Deductor Type
    bh[54] = '' // 54. State Name
    bh[55] = '' // 55. PAO Code
    bh[56] = '' // 56. DDO Code
    bh[57] = '' // 57. Ministry Name
    bh[58] = '' // 58. Ministry Other
    bh[59] = section3.permanentAccountNumber || '' // 59. PAN of Responsible Person (Matching current code's placement for PAN)
    bh[60] = '' // 60. PAO Reg
    bh[61] = '' // 61. DDO Reg
    bh[62] = '' // 62. STD Alternate
    bh[63] = '' // 63. Tel Alternate
    bh[64] = '' // 64. Email Alternate
    bh[65] = '' // 65. Responsible Person STD Alternate
    bh[66] = '' // 66. Responsible Person Tel Alternate
    bh[67] = '' // 67. Responsible Person Email Alternate
    bh[68] = '' // 68. AIN
    bh[69] = section2.gstin || '' // 69. GSTN (Aligned with spec 2.1)
    bh[70] = '' // 70. Count of Section 194P Details Records
    bh[71] = '' // 71. Batch Total of - Gross Total Income as per Section 194P Detail Records
    bh[72] = '' // 72. Record Hash (Not applicable)

    fileContent += bh.slice(1).join('^') + '\n'
    lineNumber++

    // 3. Challan Detail Records (CD)
    let actualChallanIndex = 1;
    challanData.forEach((challan) => {
      let tdsEntriesForChallan = tdsData.filter(tds => tds.challanId == challan.id)

      if (selectedReturnType === 'Correction') {
        tdsEntriesForChallan = tdsEntriesForChallan.filter(tds => {
          const original = originalTdsData.find(o => o.panOfDeductee === tds.panOfDeductee && o.amountOfPayment === tds.amountOfPayment)
          if (!original) return true;

          // Robust comparison
          const hasChanged =
            tds.panOfDeductee !== original.panOfDeductee ||
            parseFloat(tds.tdsAmount || 0) !== parseFloat(original.tdsAmount || 0) ||
            tds.sectionCode !== original.sectionCode ||
            tds.dateOfPayment !== original.dateOfPayment ||
            tds.dateOfDeduction !== original.dateOfDeduction ||
            tds.reasonForNonDeduction !== original.reasonForNonDeduction;

          return hasChanged;
        })
        if (tdsEntriesForChallan.length === 0) return
      }

      const cd = []

      cd[1] = lineNumber // 1. Line Number
      cd[2] = 'CD' // 2. Record Type
      cd[3] = '1' // 3. Batch Number
      cd[4] = actualChallanIndex // 4. Challan-Detail Record Number
      cd[5] = tdsEntriesForChallan.length // 5. Count of Deductee / Party Records
      cd[6] = 'N' // 6. NIL Challan Indicator
      cd[7] = '' // 7. Challan Updation Indicator
      cd[8] = '' // 8. Filler 3
      cd[9] = '' // 9. Filler 4
      cd[10] = '' // 10. Filler 5
      cd[11] = '' // 11. Last Bank Challan No
      cd[12] = challan.challanSerialNo || '' // 12. Bank Challan No
      cd[13] = '' // 13. Last Transfer Voucher No
      cd[14] = '' // 14. DDO Serial No
      cd[15] = '' // 15. Last Bank-Branch Code
      cd[16] = challan.bsrCode // 16. Bank-Branch Code
      cd[17] = '' // 17. Last Date
      cd[18] = formatDate(challan.dateOfDeposit) // 18. Date of Bank Challan
      cd[19] = '' // 19. Filler 6
      cd[20] = '' // 20. Filler 7
      cd[21] = '' // 21. Section

      const eduCess = parseFloat(challan.educationCess || 0) + parseFloat(challan.higherEducationCess || 0)
      cd[22] = formatDecimalAmount(challan.tdsAmount) // 22. Oltas TDS Income Tax
      cd[23] = formatDecimalAmount(challan.surcharge) // 23. Oltas TDS Surcharge
      cd[24] = formatDecimalAmount(eduCess) // 24. Oltas TDS Cess
      cd[25] = formatDecimalAmount(challan.interest) // 25. Oltas TDS Interest
      cd[26] = formatDecimalAmount(challan.other) // 26. Oltas TDS Others
      cd[27] = formatDecimalAmount(challan.totalAmount) // 27. Total of Deposit Amount
      cd[28] = '' // 28. Last Total

      const deducteeTotal = tdsEntriesForChallan.reduce((sum, tds) => sum + parseFloat(tds.tdsAmount || 0), 0)
      cd[29] = formatDecimalAmount(deducteeTotal) // 29. Total Tax Deposit Amount (Deductee sum)
      cd[30] = formatDecimalAmount(deducteeTotal) // 30. TDS Income Tax
      cd[31] = formatDecimalAmount(0) // 31. TDS Surcharge
      cd[32] = formatDecimalAmount(0) // 32. TDS Cess
      cd[33] = formatDecimalAmount(deducteeTotal) // 33. Sum of Total Income Tax
      cd[34] = formatDecimalAmount(0) // 34. TDS Interest
      cd[35] = formatDecimalAmount(0) // 35. TDS Others
      cd[36] = '' // 36. Cheque / DD No.
      cd[37] = challan.bookEntry ? 'Y' : 'N' // 37. By Book entry / Cash
      cd[38] = '' // 38. Remarks
      cd[39] = formatDecimalAmount(challan.feesAmount) // 39. Fee
      cd[40] = formatMinorHead(challan.minorHead) // 40. Minor Head
      cd[41] = '' // 41. Record Hash (Not applicable)

      fileContent += cd.slice(1).join('^') + '\n'
      lineNumber++

      // 4. Deductee Detail Records (DD)
      tdsEntriesForChallan.forEach((tds, tdsIndex) => {
        const dd = []
        let deducteeCode = tds.deducteeCode || '02'
        if (deducteeCode === '01') deducteeCode = '1'
        if (deducteeCode === '02') deducteeCode = '2'

        const rate = parseFloat(tds.rateOfTax || 0)
        const tdsVal = parseFloat(tds.tdsAmount || 0)
        const surchargeVal = parseFloat(tds.surcharge || 0)
        const cessVal = parseFloat(tds.cess || 0)
        const totalTax = tdsVal + surchargeVal + cessVal

        dd[1] = lineNumber // 1. Line Number
        dd[2] = 'DD' // 2. Record Type
        dd[3] = '1' // 3. Batch Number
        dd[4] = actualChallanIndex // 4. Challan-Detail Record Number
        dd[5] = tdsIndex + 1 // 5. Deductee / Party Detail Record No
        dd[6] = 'O' // 6. Mode
        const refNo = (tds.deducteeReferenceNo || '').toString();
        dd[7] = refNo ? refNo.padStart(9, '0') : ''; // 7. Employee Reference No. (Provided by Employer)
        dd[8] = '' // 8. Deductee / Code (Not applicable)
        dd[9] = '' // 9. Last Employee / PAN (Not applicable)
        dd[10] = String(tds.panOfDeductee || '').replace(/[\r\n]/g, '').trim() // 10. Employee / PAN
        dd[11] = '' // 11. Last  Employee/ PAN Ref. No.(Not applicable)
        dd[12] = '' // 12. PAN Ref. No.
        dd[13] = `${tds.firstName || ''} ${tds.middleName || ''} ${tds.lastName || ''}`.trim() // 13. Name of Employee /
        dd[14] = formatDecimalAmount(tdsVal) // 14. TDS / TCS -Income Tax for the period
        dd[15] = formatDecimalAmount(surchargeVal) // 15. TDS / TCS -Surcharge  for the period
        dd[16] = formatDecimalAmount(cessVal) // 16. TDS / TCS -Health and Education Cess
        dd[17] = formatDecimalAmount(totalTax) // 17. Total Income Tax Deducted at Source (TDS / TCS Income Tax+ TDS / TCS Surcharge + TDS / TCS -Cess)
        dd[18] = '' // 18. Last Total Income Tax Deducted at Source (Income Tax +Surcharge+Cess)  ( Used for Verification)  (Not applicable)
        dd[19] = formatDecimalAmount(totalTax) // 19. Total Tax Deposited
        dd[20] = '' // 20. Last Total Tax Deposited  ( Used for Verification)  (Not applicable)
        dd[21] = '' // 21. Total Value of Purchase  (Not applicable)
        dd[22] = formatDecimalAmount(tds.amountOfPayment) // 22. Amount Paid / Credited (Rs.)
        dd[23] = formatDate(tds.dateOfPayment) // 23. Date of payment / credit
        dd[24] = totalTax > 0 ? formatDate(tds.dateOfDeduction) : '' // 24. Date on which tax Deducted 
        dd[25] = formatDate(challan.dateOfDeposit) // 25. Date of Deposit
        dd[26] = '' // 26. Rate at which Tax Deducted / Collected (Not applicable)
        dd[27] = '' // 27. Grossing up Indicator  (Not applicable)
        dd[28] = '' // 28. Book Entry / Cash Indicator  (Not applicable)
        dd[29] = '' // 29. Date of furnishing Tax Deduction Certificate  (Not applicable)
        dd[30] = tds.reasonForNonDeduction || '' // 30. Remarks 1 (Reason for non-deduction / lower deduction/ higher deduction)
        dd[31] = '' // 31. Remarks 2 (For future use)
        dd[32] = '' // 32. Remarks 3 (For future use)
        dd[33] = tds.sectionCode || '' // 33. Section Code under which payment made
        dd[34] = tds.certificateNo197 || '' // 34. Certificate number issued by the Assessing Officer u/s 197

        // Fillers 35-54
        for (let i = 35; i <= 53; i++) dd[i] = ''
        dd[54] = '' // 54. Record Hash (Not applicable)

        fileContent += dd.slice(1).join('^') + '\n'
        lineNumber++
      })
      actualChallanIndex++
    })

    return fileContent
  }

  // Main function to handle Prepare Return
  

  // Conso File Parsing Logic
  const parseConsoFile = (fileContent) => {
    const lines = fileContent.split('\n')

    // Validate BH record form type
    const bhLine = lines.find(l => {
      const p = l.split('^')
      return p[1] === 'BH'
    })

    if (bhLine) {
      const bhParts = bhLine.split('^')
      const formInConso = (bhParts[4] || '').toUpperCase().trim()
      if (formInConso && formInConso !== '24Q' && formInConso !== '138') {
        toast.error(`Invalid Conso File! Expected Form 24Q, but file contains Form ${formInConso}.`)
        alert(`Invalid Conso File!\n\nThis form is Form 24Q, but the uploaded Conso file belongs to Form ${formInConso}.\nPlease upload the correct Form 24Q Conso file.`)
        return
      }
    }

    // Store content for validation
    setConsoFileContent(fileContent)

    const newChallans = []
    const newTdsData = []
    let currentChallan = null
    let rowIndex = 0

    try {
      lines.forEach((line) => {
        if (!line.trim()) return
        const parts = line.split('^')
        const recordType = parts[1] // FH, BH, CD, DD

        if (recordType === 'BH') {
          // Batch Header Parsing
          // State Code Map (ITD)
          const stateCodeMap = {
            '01': 'ANDAMAN AND NICOBAR ISLANDS', '02': 'ANDHRA PRADESH', '03': 'ARUNACHAL PRADESH', '04': 'ASSAM', '05': 'BIHAR',
            '06': 'CHANDIGARH', '07': 'DADRA AND NAGAR HAVELI', '08': 'DAMAN AND DIU', '09': 'DELHI', '10': 'GOA',
            '11': 'GUJARAT', '12': 'HARYANA', '13': 'HIMACHAL PRADESH', '14': 'JAMMU AND KASHMIR', '15': 'KARNATAKA',
            '16': 'KERALA', '17': 'LAKSHADWEEP', '18': 'MADHYA PRADESH', '19': 'MAHARASHTRA', '20': 'MANIPUR',
            '21': 'MEGHALAYA', '22': 'MIZORAM', '23': 'NAGALAND', '24': 'ORISSA', '25': 'PONDICHERRY',
            '26': 'PUNJAB', '27': 'RAJASTHAN', '28': 'SIKKIM', '29': 'TAMIL NADU', '30': 'TRIPURA',
            '31': 'UTTAR PRADESH', '32': 'WEST BENGAL', '33': 'CHHATTISGARH', '34': 'UTTARANCHAL', '35': 'JHARKHAND',
            '36': 'TELANGANA', '37': 'LADAKH', '99': 'FOREIGN'
          }

          const formType = parts[4]
          const fyRaw = parts[16] // FY
          const quarter = parts[17] // Quarter

          if (formType) {
            setSelectedForm(formType)
            localStorage.setItem('tds24QForm', formType)
          }
          if (quarter) {
            setSelectedQuarter(quarter)
            localStorage.setItem('tds24QQuarter', quarter)
          }
          if (fyRaw && fyRaw.length === 6) {
            const formattedFy = `${fyRaw.slice(0, 4)}-${fyRaw.slice(4, 6)}` // 2019-20
            setSelectedFinancialYear(formattedFy)
            localStorage.setItem('tds24QFinancialYear', formattedFy)
          }

          const getMappedState = (code) => {
            if (!code) return '';
            const padded = code.padStart(2, '0');
            return stateCodeMap[padded] || code || '';
          };

          setProfileData(prev => ({
            ...prev,
            section1: {
              ...prev.section1,
              taxDeduction: parts[12] || '', // TAN (Field 13)
              permanentAccountNumber: parts[14] || '', // PAN (Field 15)
              typeOfDeduction: parts[31] || '', // Type/Status (Field 32)
              receiptNoOriginal: parts[7] || '', // Token No (Field 8)
              receiptNoEarlier: parts[8] || '' // Prev Token No (Field 9)
            },
            section2: {
              ...prev.section2,
              name: parts[18] || '', // Field 19
              branchDivision: parts[19] || '', // Field 20
              flatNo: parts[20] || '', // Field 21
              nameOfPremises: parts[21] || '', // Field 22
              roadStreet: parts[22] || '', // Field 23
              areaLocality: parts[23] || '', // Field 24
              townCity: parts[24] || '', // Field 25
              state: getMappedState(parts[25]), // Field 26
              pinCode: parts[26] || '', // Field 27
              email: parts[27] || '', // Field 28
              stdCode: parts[28] || '', // Field 29
              telephoneNo: parts[29] || '', // Field 30
              gstin: parts[68] || '', // Field 69
            },
            section3: {
              ...prev.section3,
              name: parts[32] || '', // Field 33
              designation: parts[33] || '', // Field 34
              flatNo: parts[34] || '', // Field 35
              nameOfPremises: parts[35] || '', // Field 36
              roadStreet: parts[36] || '', // Field 37
              areaLocality: parts[37] || '', // Field 38
              townCity: parts[38] || '', // Field 39
              state: getMappedState(parts[39]), // Field 40
              pinCode: parts[40] || '', // Field 41
              email: parts[41] || '', // Field 42
              mobileNo: parts[47] || '', // Field 48 (TRACES Conso)
              telephone: parts[44] || '', // Field 45
              telephoneAlternate: parts[45] || '', // Field 46
              permanentAccountNumber: parts[58] || '', // Field 59
              hasAddressChanged: parts[46] === 'Y' ? 'Yes' : 'No' // Field 47
            }
          }))
        } else if (recordType === 'CD') {
          // Challan Detail
          // Sample: 3^CD^1^1^20^N^^21^M^^^57858^^^^0510308^^29042022^^^^000000791663.00...
          // Map parts to challan object
          const challan = {
            id: Date.now() + rowIndex++,
            challanSerialNo: parts[11] || '', // F12
            bsrCode: parts[15] || '', // F16
            dateOfDeposit: parts[17] ? `${parts[17].slice(4, 8)}-${parts[17].slice(2, 4)}-${parts[17].slice(0, 2)}` : '', // DDMMYYYY -> YYYY-MM-DD
            minorHead: parts[40] || '', // F40
            sectionCode: '', // CD doesn't strictly have Section Code in F1? 
            // Wait, CD F1-F6 are control.
            // Challan Amount usually F27 (Total).
            tdsAmount: parseFloat(parts[21] || 0), // F22 TDS
            surcharge: parseFloat(parts[22] || 0), // F23
            educationCess: parseFloat(parts[23] || 0), // F24 (Ed + HighEd)
            interest: parseFloat(parts[24] || 0), // F25
            other: parseFloat(parts[25] || 0), // F26
            totalAmount: parseFloat(parts[26] || 0), // F27
            feesAmount: parseFloat(parts[38] || 0), // F39
            bookEntry: parts[36] === 'Y', // F37
            recordNo: '',
            // Initialize others
            higherEducationCess: 0
          }
          currentChallan = challan
          newChallans.push(challan)

        } else if (recordType === 'DD') {
          // Deductee Detail - Corrected Indices based on Sample 1236
          if (!currentChallan) return

          const fullName = (parts[12] || '').trim();
          const deductee = {
            id: Date.now() + rowIndex++,
            challanId: currentChallan.id,
            deducteeCode: parts[7] === '1' ? '01' : '02',
            panOfDeductee: parts[9] || '',
            firstName: fullName,
            middleName: '',
            lastName: '',
            nameOfDeductee: parts[12] || '',
            amountOfPayment: parseFloat(parts[21] || 0),
            dateOfPayment: parts[22] ? `${parts[22].slice(4, 8)}-${parts[22].slice(2, 4)}-${parts[22].slice(0, 2)}` : '',
            dateOfDeduction: parts[23] ? `${parts[23].slice(4, 8)}-${parts[23].slice(2, 4)}-${parts[23].slice(0, 2)}` : '',
            rateOfTax: parseFloat(parts[25] || 0).toFixed(2), // Raw value is %

            tdsAmount: parseFloat(parts[13] || 0),
            surcharge: parseFloat(parts[14] || 0),
            cess: parseFloat(parts[15] || 0),
            sectionCode: parts[33] || '',
            reasonForNonDeduction: parts[29] || '',
            certificateNo197: parts[34] || '',
            recordNo: parts[4] || '',
            source: 'conso', // Mark as from Conso file
          }

          // No reverse mapping needed for 24Q as section codes (92A, 92B) match UI values
          // deductee.sectionCode = deductee.sectionCode

          newTdsData.push(deductee)
        }
      })

      // Post-process Challans to auto-fill Section Code from their Deductees
      newChallans.forEach(challan => {
        const matchingDeductee = newTdsData.find(tds => tds.challanId === challan.id)
        if (matchingDeductee && matchingDeductee.sectionCode) {
          if (!challan.sectionCode) {
            challan.sectionCode = matchingDeductee.sectionCode
          }
        }
      })

      setChallanData(newChallans)
      setTdsData(newTdsData)
      setOriginalTdsData(JSON.parse(JSON.stringify(newTdsData))) // Deep copy for comparison source
      toast.success('Conso File Uploaded and Parsed Successfully!')

    } catch (err) {
      console.error(err)
      toast.error('Error parsing conso file')
    }
  }

  const handleConsoFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      setConsoFileContent(ev.target.result)
      parseConsoFile(ev.target.result)
    }
    reader.readAsText(file)
  }

  const handleCsiFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      if (!file.name.endsWith('.csi')) {
        toast.error('Please select a valid .csi file')
        return
      }
      setCsiFile(file)
      if (typeof setShowCsiUploadPopup === 'function') setShowCsiUploadPopup(false)
      toast.success(`CSI File selected: ${file.name}. Generating FVU...`)
      if (typeof executeValidation === 'function') {
        executeValidation(file)
      }
    }
  }

  // Financial Year, Quarter and Form options
  // Financial Year, Quarter and Form options
  const today = new Date()
  const currMonth = today.getMonth() // 0=Jan, 3=Apr
  const currYear = today.getFullYear()

  const currentFyStart = currMonth >= 3 ? currYear : currYear - 1
  
  const financialYearOptions = []
  for (let start = currentFyStart; start >= 2023; start--) {
    financialYearOptions.push(`${start}-${(start + 1).toString().slice(-2)}`)
  }

  // Generate Quarter Options based on selected FY
  const selectedFyStart = parseInt(selectedFinancialYear.split('-')[0])
  let quarterOptions = ['Q1', 'Q2', 'Q3', 'Q4']

  if (selectedFyStart > currentFyStart) {
    quarterOptions = []
  } else if (selectedFyStart === currentFyStart) {
    // Filter quarters based on current month
    let maxQ = 0
    if (currMonth >= 3 && currMonth <= 5) maxQ = 1 // Apr-Jun (Q1)
    else if (currMonth >= 6 && currMonth <= 8) maxQ = 2 // Jul-Sep (Q2)
    else if (currMonth >= 9 && currMonth <= 11) maxQ = 3 // Oct-Dec (Q3)
    else maxQ = 4 // Jan-Mar (Q4)

    quarterOptions = quarterOptions.slice(0, maxQ)
  }
  const formOptions = ['24Q', '26Q', '27Q', '27EQ']

  // Dropdown options for profile form - matching original TdsProfile

  const yesNoOptions = ['Yes', 'No']

  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      const initialProfile = {
        section1: {
          taxDeduction: '',
          permanentAccountNumber: '',
          typeOfDeduction: '',
          receiptNoOriginal: '',
          receiptNoEarlier: '',
          isEarlierReturnFiled: 'No'
        },
        section2: {
          name: '',
          branchDivision: '',
          flatNo: '',
          nameOfPremises: '',
          roadStreet: '',
          areaLocality: '',
          townCity: '',
          pinCode: '',
          state: '',
          email: '',
          stdCode: '',
          telephoneNo: ''
        },
        section3: {
          sameAsAbove: false,
          name: '',
          designation: '',
          permanentAccountNumber: '',
          nameOfPremises: '',
          flatNo: '',
          areaLocality: '',
          roadStreet: '',
          pinCode: '',
          townCity: '',
          telephone: '',
          telephoneAlternate: '',
          state: '',
          email: '',
          emailAlternate: '',
          mobileNo: '',
          hasAddressChanged: '',
          hasRegularStatement: ''
        }
      }

      setChallanData([])
      setTdsData([])
      setOriginalTdsData([])
      setProfileData(initialProfile)

      localStorage.removeItem('tds24QChallanData')
      localStorage.removeItem('tds24QTdsData')
      localStorage.removeItem('tds24QProfileData')
      localStorage.removeItem('tds24QOriginalTdsData')

      toast.success('All data cleared successfully')
    }
  }

  const handleQuarterChange = (newQuarter) => {
    setSelectedQuarter(newQuarter);
    localStorage.setItem('tdsQuarter', newQuarter);
  }

  useEffect(() => {
    if (selectedReturnType === 'Correction' || selectedReturnType === 'Revised') {
      navigate('/tds-dashboard/24q-correction');
    }
  }, [selectedReturnType, navigate]);

  if (selectedQuarter === 'Q4') {
    return (
      <Tds24Q4Form
        isMobile={isMobile}
        onQuarterChange={handleQuarterChange}
      />
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 w-full overflow-x-hidden">
      {/* Sidebar */}
      <TdsSidebar
        isOpen={sidebarOpen}
        isMobile={isMobile}
        onClose={handleSidebarClose}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        onSidebarStateChange={handleSidebarStateChange}
        tdsData={tdsData}
        challanData={challanData}
      />

      {/* Header */}
      <header className={`bg-white shadow-sm border-b transition-all duration-300 ${!isMobile ? (sidebarState.isExpanded ? 'ml-64' : 'ml-14') : ''
        } `} style={{
          width: !isMobile ? (sidebarState.isExpanded ? 'calc(100% - 16rem)' : 'calc(100% - 3.5rem)') : '100%'
        }}>
        <div className="w-full px-2 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center h-16 relative">
            <div className="flex items-center">
            </div>
            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <h1 className="text-xl font-bold text-gray-800">Form 24Q (Salary)</h1>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={handleClearData}
                className="flex items-center text-red-600 hover:text-red-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Data
              </button>

            </div>
          </div>
        </div>
      </header >

      {/* Main Content */}
      <main className={`py-4 px-2 sm:px-4 lg:px-6 transition-all duration-300 ${!isMobile ? (sidebarState.isExpanded ? 'ml-64' : 'ml-14') : ''
        } `} style={{
          width: !isMobile ? (sidebarState.isExpanded ? 'calc(100% - 16rem)' : 'calc(100% - 3.5rem)') : '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          minHeight: 'calc(100vh - 4rem)'
        }}>


        {/* Financial Year and Form Selection */}
        < div className="mb-8 bg-white rounded-lg shadow-md p-6" >
          <div className="flex flex-col lg:flex-row w-full gap-4 items-end">
            <div className="flex-1 min-w-[180px] w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Financial Year (FY) <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedFinancialYear}
                onChange={(e) => {
                  const newYear = e.target.value;
                  setSelectedFinancialYear(newYear);
                  localStorage.setItem('tdsFinancialYear', newYear);
                  
                  // Auto-update selectedForm if it doesn't match the year's format
                  if (newYear) {
                    const isNewFormat = parseInt(newYear.split('-')[0]) >= 2026;
                    setSelectedForm(prevForm => {
                      const f = prevForm;
                      const mapped = isNewFormat 
                        ? (f === '24Q' ? '138' : (f === '26Q' || f === '140') ? '140' : f === '27Q' ? '144' : f === '27EQ' ? '143' : f === '24Q4' ? '24Q4' : f)
                        : (f === '138' ? '24Q' : (f === '140' || f === '26Q') ? '26Q' : f === '144' ? '27Q' : f === '143' ? '27EQ' : f === '24Q4' ? '24Q4' : f);
                      if (mapped !== prevForm) {
                        localStorage.setItem('tdsForm', mapped);
                        return mapped;
                      }
                      return prevForm;
                    });
                  }
                }}
                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
              >
                <option value="">Select Financial Year</option>
                {financialYearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[180px] w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quarter <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedQuarter}
                onChange={(e) => {
                  setSelectedQuarter(e.target.value)
                  localStorage.setItem('tdsQuarter', e.target.value)
                }}
                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
              >
                <option value="">Select Quarter</option>
                {quarterOptions.map(quarter => (
                  <option key={quarter} value={quarter}>{quarter}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[180px] w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Form Type <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedForm}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedForm(val)
                  localStorage.setItem('tdsForm', val)
                  const targetPath = getTdsFormRoute(val, selectedReturnType, selectedFinancialYear)
                  navigate(targetPath)
                }}
                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
              >
                {formOptions.map(formVal => {
                    const f = formVal;
                    const actualForm = selectedFinancialYear && parseInt(selectedFinancialYear.split('-')[0]) >= 2026 
                        ? (f === '24Q' ? '138' : (f === '26Q' || f === '140') ? '140' : f === '27Q' ? '144' : f === '27EQ' ? '143' : f)
                        : (f === '138' ? '24Q' : (f === '140' || f === '26Q') ? '26Q' : f === '144' ? '27Q' : f === '143' ? '27EQ' : f);
                    return (
                      <option key={formVal} value={actualForm}>
                        {actualForm}
                      </option>
                    )
                  })}
              </select>
            </div>

            <div className="flex-1 min-w-[180px] w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Return Type <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedReturnType}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedReturnType(val)
                  localStorage.setItem('SELECTED_RETURN_TYPE', val)
                  const targetPath = getTdsFormRoute(selectedForm, val, selectedFinancialYear)
                  navigate(targetPath)
                }}
                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
              >
                <option value="">Select Return Type</option>
                <option value="Regular">Regular</option>
                <option value="Correction">Correction</option>
              </select>
            </div>

            {/* Premium Apply Selection and Conso Trigger */}
            <div className="flex-1 min-w-[200px] w-full flex items-center mb-1">
              <button
                onClick={() => {
                  const targetForm = selectedForm ? selectedForm.toLowerCase() : '24q';
                  const isCorrection = selectedReturnType === 'Correction';
                  const suffix = isCorrection ? '-correction' : '';
                  const targetPath = `/tds-dashboard/${targetForm}${suffix}`;
                  if (location.pathname !== targetPath) {
                    navigate(targetPath);
                    toast.success('Selection Applied');
                  } else {
                    toast.success('Selection Applied');
                  }
                }}
                className="w-full px-8 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all font-semibold shadow-lg hover:shadow-orange-200"
              >
                Apply Selection
              </button>
            </div>
          </div>
        </div>

        {/* Receipt Details Section (Conditional) */}
        {(selectedReturnType === 'Correction' || selectedReturnType === 'Regular') && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100 overflow-hidden relative">
            <div className="absolute top-0 left-0 w-2 h-full bg-orange-500"></div>
            <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
              <Building2 className="w-5 h-5 mr-2 text-orange-600" />
              Statement Receipt Details <span className="text-red-500 ml-1">*</span>
            </h3>

            <div className="space-y-6">
              {/* REVISED RETURN CASE - PREMIUM UI */}
              {selectedReturnType === 'Correction' && (
                <div className="bg-orange-50 rounded-xl p-6 border border-orange-100">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    {/* Conso Upload Area */}
                    <div className="lg:col-span-1">
                      <div className="flex flex-col items-center">
                        <input
                          type="file"
                          accept=".tds"
                          onChange={handleConsoFileUpload}
                          className="hidden"
                          id="conso-upload-premium"
                        />
                        <label
                          htmlFor="conso-upload-premium"
                          className="group relative flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-orange-300 rounded-2xl bg-white hover:bg-orange-50 hover:border-orange-500 transition-all cursor-pointer overflow-hidden shadow-sm"
                        >
                          {consoFileContent ? (
                            <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="flex flex-col items-center text-green-600">
                              <div className="bg-green-100 p-3 rounded-full mb-2">
                                <FileText className="w-8 h-8" />
                              </div>
                              <span className="text-sm font-bold">Conso File Ready</span>
                              <span className="text-xs text-green-500 mt-1">Click to Replace</span>
                            </motion.div>
                          ) : (
                            <div className="flex flex-col items-center text-gray-500">
                              <div className="bg-orange-100 p-3 rounded-full mb-3 group-hover:bg-orange-200 transition-colors">
                                <Upload className="w-8 h-8 text-orange-600" />
                              </div>
                              <span className="text-sm font-bold text-gray-700 group-hover:text-orange-700">Upload Conso File</span>
                              <span className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider font-medium">Click to browse (.tds)</span>
                            </div>
                          )}

                          {/* Animated background glow */}
                          <div className="absolute inset-0 bg-blue-400 opacity-0 group-hover:opacity-5 transition-opacity pointer-events-none"></div>
                        </label>

                        <p className="mt-3 text-xs text-gray-500 text-center flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          Upload Consolidated .tds file from TRACES
                        </p>
                      </div>
                    </div>

                    {/* Auto-filled Fields */}
                    <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-orange-700 uppercase tracking-wider">
                          Receipt No. of Original Return
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={profileData?.section1?.receiptNoOriginal || ''}
                            disabled
                            className="w-full px-4 py-3 bg-white border border-orange-200 rounded-xl text-gray-700 font-medium shadow-sm cursor-not-allowed"
                            placeholder="Waiting for Conso upload..."
                          />
                          {profileData?.section1?.receiptNoOriginal && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                              Verified
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-orange-700 uppercase tracking-wider">
                          Receipt No. of Earlier Statement
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={profileData?.section1?.receiptNoEarlier || ''}
                            disabled
                            className="w-full px-4 py-3 bg-white border border-orange-200 rounded-xl text-gray-700 font-medium shadow-sm cursor-not-allowed"
                            placeholder="Waiting for Conso upload..."
                          />
                          {profileData?.section1?.receiptNoEarlier && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                              Verified
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="md:col-span-2 flex items-center p-3 bg-white rounded-lg border border-orange-100 shadow-sm">
                        <div className="bg-orange-100 p-2 rounded-lg mr-3">
                          <ArrowLeft className="w-4 h-4 text-orange-600" />
                        </div>
                        <p className="text-xs text-gray-600">
                          <span className="font-bold text-orange-600">Note:</span> These details are auto-extracted from your Conso file to ensure correctness and prevent validation failures.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedReturnType === 'Regular' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Is earlier Return Filed?
                    </label>
                    <select
                      value={profileData?.section1?.isEarlierReturnFiled || 'No'}
                      onChange={(e) => handleProfileInputChange('section1.isEarlierReturnFiled', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                  {profileData?.section1?.isEarlierReturnFiled === 'Yes' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Receipt No. of Original Return <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={profileData?.section1?.receiptNoOriginal || ''}
                        onChange={(e) => handleProfileInputChange('section1.receiptNoOriginal', e.target.value)}
                        required
                        className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                        placeholder="Enter 15-digit Receipt Number"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section Content */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {activeSection === 'tds-details' && (
            <TdsDetailsTable
              tdsData={tdsData}
              challanData={challanData}
              onTdsDataChange={handleTdsDataChange}
              sectionOptions={selectedFinancialYear && parseInt(selectedFinancialYear.split('-')[0]) >= 2026 ? sectionOptions24Q_new : sectionOptions24Q_old}
              selectedFinancialYear={selectedFinancialYear}
              selectedQuarter={selectedQuarter}
              isRevised={selectedReturnType === 'Correction'}
              originalData={originalTdsData}
              hideRateColumn={true}
              is24Q={true}
            />
          ) || activeSection === 'challan-details' && (

            <ChallanDetailsTable
              challanData={challanData}
              tdsData={tdsData}
              onChallanDataChange={handleChallanDataChange}
              tan={profileData?.section1?.taxDeduction}
              financialYear={selectedFinancialYear}
              sectionOptions={selectedFinancialYear && parseInt(selectedFinancialYear.split('-')[0]) >= 2026 ? sectionOptions24Q_new : sectionOptions24Q_old}
            />
          )}

          {activeSection === 'profile' && (
            <TdsProfileSection
              profileData={profileData}
              isProfileEditing={isProfileEditing}
              onProfileDataChange={handleProfileInputChange}
              onPrefillClick={() => setShowPrefillPrompt(true)}
              selectedReturnType={selectedReturnType}
            />
          )}
        </div>





        {/* CSI Confirmation Modal */}
        {
          showCsiUploadPopup && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
              <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl animate-fade-in border border-gray-100">
                <h3 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">Upload CSI File</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  To proceed with validation, you need to upload the <strong>Challan Status Inquiry (CSI)</strong> file.
                  <br /><br />
                  Please ensure you have downloaded it from the Income Tax portal.
                </p>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setShowCsiUploadPopup(false)}
                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowCsiUploadPopup(false)
                      if (csiFileInputRef.current) {
                        csiFileInputRef.current.value = ''
                        csiFileInputRef.current.click()
                      }
                    }}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center transition-colors duration-200 shadow-md"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload CSI File
                  </button>
                </div>
              </div>
            </div>
          )
        }

        <input
          type="file"
          ref={csiFileInputRef}
          style={{ display: 'none' }}
          accept=".csi"
          onChange={handleCsiFileChange}
        />

        {/* CSI Download Prompt Modal */}
        {
          showDownloadPrompt && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <Upload className="w-5 h-5 mr-2 text-blue-600" />
                  Download CSI File
                </h3>

                <p className="text-gray-600 mb-4 text-sm">
                  Would you like to automatically download the latest CSI file from the Income Tax Portal?
                </p>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Income Tax Password</label>
                  <div className="relative">
                    <input
                      type={showDownloadPassword ? "text" : "password"}
                      className="w-full border border-gray-300 rounded-md p-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      value={downloadPassword}
                      onChange={(e) => setDownloadPassword(e.target.value)}
                      placeholder="Enter Password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowDownloadPassword(!showDownloadPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                    >
                      {showDownloadPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">TAN: {profileData.section1.taxDeduction}</p>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={handleCsiDownload}
                    disabled={isDownloadingCsi}
                    className={`flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center border border-transparent shadow-md ${isDownloadingCsi ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {isDownloadingCsi ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Downloading...
                      </>
                    ) : (
                      <span className="font-medium">Download & Continue</span>
                    )}
                  </button>
                  <button
                    onClick={handleSkipDownload}
                    disabled={isDownloadingCsi}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition"
                  >
                    Skip (I have file)
                  </button>
                </div>
                <button
                  onClick={() => setShowDownloadPrompt(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )
        }

        <ToastContainer position="top-right" autoClose={3000} />
        {/* Validation Lock Screen Overlay */}
        {
          isValidating && (
            <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gray-900 bg-opacity-80 backdrop-blur-sm cursor-wait">
              <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center max-w-lg text-center transform transition-all scale-100">
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-orange-100 rounded-full animate-ping opacity-75"></div>
                  <div className="relative bg-white rounded-full p-2">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-200 border-t-orange-600"></div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-800 mb-2">Validation in Progress</h3>
                <p className="text-gray-600 mb-6">
                  Please wait while we validate your return.
                </p>
                <button
                  onClick={handleCancelValidation}
                  className="px-6 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition-colors border border-red-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )
        }
        {/* Profile Pre-fill Prompt Modal */}
        {
          showPrefillPrompt && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-blue-600" />
                  Pre-fill Profile Data
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Please enter your <strong>Income Tax Portal Password</strong> for TAN: <strong>{profileData.section1?.taxDeduction}</strong> to auto-fill PAN, Mobile, Email & Address.
                </p>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                  <div className="relative">
                    <input
                      type={showPrefillPassword ? "text" : "password"}
                      value={prefillPassword}
                      onChange={(e) => setPrefillPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      placeholder="Enter Portal Password"
                      onKeyPress={(e) => e.key === 'Enter' && handleProfilePrefill()}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPrefillPassword(!showPrefillPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                    >
                      {showPrefillPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPrefillPrompt(false)}
                    disabled={isExtractingProfile}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-bold transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleProfilePrefill}
                    disabled={isExtractingProfile}
                    className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-bold transition-colors shadow-lg disabled:bg-indigo-300 disabled:cursor-not-allowed flex items-center justify-center min-w-[140px]"
                  >
                    {isExtractingProfile ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Get Data...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Get Data
                      </>
                    )}
                  </button>
                </div>

                <button
                  onClick={() => setShowPrefillPrompt(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )
        }

      </main>
    </div>
  )
}

export default Tds24QForm

